// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Outsize — human-readable stat values. Rounded to ~3 significant figures so a
// dated population estimate or a computed area is not shown to false precision.

import type { StatId } from './data';

/** Round to n significant figures. */
function sig(n: number, digits = 3): number {
  if (n === 0) return 0;
  const m = Math.pow(10, digits - 1 - Math.floor(Math.log10(Math.abs(n))));
  return Math.round(n * m) / m;
}

/** A population, e.g. "1.4 billion", "67.1 million", "389,000". */
export function fmtPop(n: number): string {
  if (n >= 1e9) return `${sig(n / 1e9)} billion`;
  if (n >= 1e6) return `${sig(n / 1e6)} million`;
  return sig(n).toLocaleString('en-US');
}

/** An area in km², e.g. "17.1M km²", "552,000 km²", "274 km²". */
export function fmtArea(n: number): string {
  if (n >= 1e6) return `${sig(n / 1e6)}M km²`;
  return `${sig(n).toLocaleString('en-US')} km²`;
}

/** People per km², e.g. "1,300 /km²", "34 /km²". */
export function fmtDensity(n: number): string {
  return `${sig(n, n >= 100 ? 3 : 2).toLocaleString('en-US')} /km²`;
}

export function fmtStat(value: number, stat: StatId): string {
  if (stat === 'pop') return fmtPop(value);
  if (stat === 'area') return fmtArea(value);
  return fmtDensity(value);
}

/** The raw number for the count-up animation target (already the true value). */
export function statNumberLabel(value: number, stat: StatId): string {
  return fmtStat(value, stat);
}
